import { useEffect, useState, useRef } from 'react';
import getGame from '../api/getGame';
import checkMatch from '../api/checkMatch';
import getActorById from '../api/getActorById';
import getMediaById from '../api/getMediaById';
import submitCompletedPuzzle from '../api/submitCompletedPuzzle';
import assignRoles from '../utils/assignRoles';
import { checkCurrentMemory, setMemory, resetSnakeDay } from '../utils/localStorage';
import { getUserYesterdayDate, getUserCurrentDate } from '../utils/getUserCurrentDate';
import incrementPuzzPlayed from '../api/incrementPuzzPlayed';
import incrementQScore from '../api/incrementQScoreV2';
import forceRefreshOnce from '../utils/forceRefresh';
import isDateToday from '../utils/isDateToday';
import createShareURL from '../utils/createShareURL';
import getHint from '../api/getHint';

function useGameplay({ setShowHelp, setShowResult, setShowGoBack, setShowTutorial }) {
    // Must run before any state initialization (same as original render-time call)
    forceRefreshOnce();

    // ── State (initialized from localStorage exactly once via function form) ───

    const [head, setHead] = useState(() => {
        const mem = checkCurrentMemory();
        return mem.head || {};
    });
    const [tail, setTail] = useState(() => {
        const mem = checkCurrentMemory();
        return mem.tail || {};
    });
    const [direction, setDirection] = useState('fwd');
    const [goBackDir, setGoBackDir] = useState('fwd');
    const [blocksFwd, setBlocksFwd] = useState(() => {
        const mem = checkCurrentMemory();
        const today = getUserCurrentDate();
        if (mem.lastDayPlayed === today) return mem.blocksFwd || [];
        return [];
    });
    const [blocksBck, setBlocksBck] = useState(() => {
        const mem = checkCurrentMemory();
        const today = getUserCurrentDate();
        if (mem.lastDayPlayed === today) return mem.blocksBck || [];
        return [];
    });
    const [strikes, setStrikes] = useState(() => {
        const mem = checkCurrentMemory();
        const today = getUserCurrentDate();
        if (mem.lastDayPlayed === today) return mem.strikes || [];
        return [];
    });
    const [gameStatus, setGameStatus] = useState(() => {
        const mem = checkCurrentMemory();
        return mem.gameStatus || 'in progress';
    });
    const [guessStatus, setGuessStatus] = useState(null);
    const [puzzleTime, setPuzzleTime] = useState('');
    const [puzzleInfo, setPuzzleInfo] = useState(() => {
        const mem = checkCurrentMemory();
        const today = getUserCurrentDate();
        if (mem.lastDayPlayed === today) return mem.puzzleInfo || {};
        return {};
    });
    const controlStatusTimerRef = useRef(null);

    // ── Mount: restore today's game or start fresh ────────────────────────────

    useEffect(() => {
        document.title = 'CineSnake';
        const checkFirstVisit = () => {
            const memory = checkCurrentMemory();
            if (!memory.tutorialSeen) {
                setShowTutorial(true);
            }
            const todaysPuzzle = isDateToday(memory?.puzzleInfo?.date);
            if (memory) {
                if (todaysPuzzle) {
                    setBlocksBck(memory.blocksBck || []);
                    setBlocksFwd(memory.blocksFwd || []);
                    setStrikes(memory.strikes || []);
                    setGameStatus(memory.gameStatus);
                    setHead(memory.head);
                    setTail(memory.tail);
                } else {
                    newSnakeDay();
                }
            } else {
                setShowHelp(true);
            }
        };
        checkFirstVisit();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Persistence effects ───────────────────────────────────────────────────

    useEffect(() => {
        if (blocksFwd.length > 0) setMemory({ blocksFwd });
    }, [blocksFwd]);

    useEffect(() => {
        if (puzzleInfo.id) setMemory({ puzzleInfo });
    }, [puzzleInfo]);

    useEffect(() => {
        if (blocksBck.length > 0) setMemory({ blocksBck });
    }, [blocksBck]);

    useEffect(() => {
        if (strikes?.length > 0) setMemory({ strikes });
        if (strikes?.length === 3) {
            setGameStatus('game over');
            setMemory({ gameStatus: 'game over', consecutiveWins: 0 });
        }
    }, [strikes]);

    useEffect(() => {
        setMemory({ gameStatus });
    }, [gameStatus]);

    // ── Unmount cleanup ───────────────────────────────────────────────────────

    useEffect(() => {
        return () => clearTimeout(controlStatusTimerRef.current);
    }, []);

    // ── Handlers ─────────────────────────────────────────────────────────────

    const controlStatus = (newBlock, success) => {
        clearTimeout(controlStatusTimerRef.current);
        setGuessStatus({ id: newBlock.id, success });
        controlStatusTimerRef.current = setTimeout(() => setGuessStatus(null), 5000);
    };

    const getUsedIds = () => {
        const usedIds = { ACTOR: [], MEDIA: [] };
        head.type === 'ACTOR' ? usedIds.ACTOR.push(head.id) : usedIds.MEDIA.push(head.id);
        tail.type === 'ACTOR' ? usedIds.ACTOR.push(tail.id) : usedIds.MEDIA.push(tail.id);
        blocksFwd.forEach(b => b.type === 'ACTOR' ? usedIds.ACTOR.push(b.id) : usedIds.MEDIA.push(b.id));
        blocksBck.forEach(b => b.type === 'ACTOR' ? usedIds.ACTOR.push(b.id) : usedIds.MEDIA.push(b.id));
        return usedIds;
    };

    // checkFinal intentionally captures blocksFwd/blocksBck from closure at
    // call time (before the new block is added to state) — this is correct.
    const checkFinal = async (newBlock) => {
        let otherSide;
        if (direction === 'fwd') {
            otherSide = blocksBck.length === 0 ? tail : blocksBck[blocksBck.length - 1];
        } else {
            otherSide = blocksFwd.length === 0 ? head : blocksFwd[blocksFwd.length - 1];
        }
        if (newBlock.type === otherSide.type) return false;
        const roles = assignRoles(newBlock, otherSide);
        const overlap = await checkMatch(roles.actor_id, roles.media_id);
        if (overlap) {
            setGameStatus('game won');
            setShowResult(true);
            // Fresh memory read to get current consecutiveWins
            const memory = checkCurrentMemory();
            const yesterday = getUserYesterdayDate();
            const today = getUserCurrentDate();
            const puzzleId = memory.puzzleInfo.id;
            const fullPuzz = createShareURL([...blocksFwd, newBlock, ...blocksBck], head, tail, strikes, true);
            const puzzleStats = await submitCompletedPuzzle(puzzleId, fullPuzz);
            const updatedPuzzleInfo = { ...puzzleInfo, ...puzzleStats };
            setPuzzleInfo(updatedPuzzleInfo);
            setMemory({
                consecutiveWins: memory.lastWin === yesterday ? memory.consecutiveWins + 1 : 1,
                lastWin: today,
                gameStatus: 'game won',
                puzzleInfo: updatedPuzzleInfo,
            });
        }
    };

    const check = async (newBlock, isHint = false) => {
        let most_recent;
        if (direction === 'fwd' && blocksFwd.length === 0) {
            most_recent = head;
        } else if (direction === 'bck' && blocksBck.length === 0) {
            most_recent = tail;
        } else {
            most_recent = direction === 'fwd' ? blocksFwd[blocksFwd.length - 1] : blocksBck[blocksBck.length - 1];
        }
        const roles = assignRoles(most_recent, newBlock);
        const overlap = await checkMatch(roles.actor_id, roles.media_id);
        if (!isHint) {
            incrementQScore(newBlock.id, newBlock.type, puzzleInfo.id);
        }
        if (overlap) {
            controlStatus(newBlock, true);
            if (direction === 'fwd') {
                setBlocksFwd([...blocksFwd, newBlock]);
            } else {
                setBlocksBck([...blocksBck, newBlock]);
            }
            checkFinal(newBlock);
        } else {
            controlStatus(newBlock, false);
            const idx = direction === 'fwd' ? blocksFwd.length : blocksBck.length;
            setStrikes([...strikes, { idx, direction, strikeInfo: { goBack: false, ...newBlock } }]);
        }
    };

    // goBack accepts idx so showGoBack (modal state in Gameplay.js) stays there
    const goBack = (idx) => {
        const oldArr = goBackDir === 'fwd' ? blocksFwd : blocksBck;
        const newBlocks = oldArr.slice(0, idx);
        if (goBackDir === 'fwd') {
            setBlocksFwd(newBlocks);
        } else {
            setBlocksBck(newBlocks);
        }
        const most_recent = oldArr.length === 0
            ? (goBackDir === 'fwd' ? head : tail)
            : oldArr[oldArr.length - 1];
        checkFinal(most_recent);
        setStrikes([...strikes, { direction: goBackDir, idx, strikeInfo: { goBack: true } }]);
        setShowGoBack(-1);
    };

    async function getTodaysPuzzle() {
        setGameStatus('loading');
        try {
            const data = await getGame();
            let newHead, newTail;
            const playCount = data.play_count;
            if (data.head_type === 'MEDIA') {
                newHead = await getMediaById(data.head);
                newHead.type = 'MEDIA';
            } else {
                newHead = await getActorById(data.head);
                newHead.type = 'ACTOR';
            }
            if (data.tail_type === 'MEDIA') {
                newTail = await getMediaById(data.tail);
                newTail.type = 'MEDIA';
            } else {
                newTail = await getActorById(data.tail);
                newTail.type = 'ACTOR';
            }
            if (data) {
                setHead(newHead);
                setTail(newTail);
                const lastDayPlayed = getUserCurrentDate();
                setMemory({ head: newHead, tail: newTail, playCount, lastDayPlayed });
                setGameStatus('in progress');
                setPuzzleTime(new Date());
                setPuzzleInfo(data);
                await incrementPuzzPlayed(data.id);
                setMemory({ puzzleInfo: data });
            } else {
                console.error('Error: Data is undefined or null.', data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }

    const triggerHint = async () => {
        const currentBlock = direction === 'fwd'
            ? (blocksFwd.length === 0 ? head : blocksFwd[blocksFwd.length - 1])
            : (blocksBck.length === 0 ? tail : blocksBck[blocksBck.length - 1]);
        const usedIds = getUsedIds();
        const relevantIds = usedIds[currentBlock.type === 'ACTOR' ? 'MEDIA' : 'ACTOR'];
        const hintBlock = await getHint(currentBlock.type, currentBlock.id, relevantIds);
        if (direction === 'fwd') {
            setBlocksFwd([...blocksFwd, hintBlock]);
        } else {
            setBlocksBck([...blocksBck, hintBlock]);
        }
        setStrikes([
            ...strikes,
            {
                direction,
                idx: direction === 'fwd' ? blocksFwd.length : blocksBck.length,
                strikeInfo: { hintUsed: true, ...hintBlock },
            },
        ]);
        checkFinal(hintBlock);
    };

    const newSnakeDay = async () => {
        setBlocksFwd([]);
        setBlocksBck([]);
        setStrikes([]);
        setHead({});
        setTail({});
        resetSnakeDay();
        await getTodaysPuzzle();
    };

    const markTutorialSeen = () => setMemory({ tutorialSeen: true });

    return {
        head, tail,
        blocksFwd, blocksBck,
        direction, setDirection,
        goBackDir, setGoBackDir,
        strikes, gameStatus,
        guessStatus, puzzleInfo, puzzleTime,
        check, goBack, triggerHint, getTodaysPuzzle, getUsedIds, markTutorialSeen,
    };
}

export default useGameplay;
